import { useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { Copy, Check, ExternalLink, PartyPopper } from 'lucide-react'

import { onboardingStyles as s, ACCENT } from '../onboardingStyles'

// A phone-shaped frame that hosts a live iframe of the user's real public page.
const previewFrame = {
    position: 'relative',
    mx: 'auto',
    width: { xs: '240px', sm: '270px' },
    height: { xs: '480px', sm: '560px' },
    border: '10px solid #000',
    borderRadius: '36px',
    overflow: 'hidden',
    backgroundColor: '#000',
    boxShadow: '0 6px 16px rgba(0,0,0,0.25)',
}

export default function LivePageStep({ userData, goNext }) {
    const username = userData?.username
    const [copied, setCopied] = useState(false)

    const shareUrl = `https://vibio.co/${username}`
    // Same-origin preview of the real public page; it fetches its own data (incl. the processed
    // video once ready), so this always reflects what was saved during onboarding.
    const previewSrc = `/${username}?preview=true`

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // ignore clipboard failures
        }
    }

    const handleView = () => {
        window.open(previewSrc, '_blank', 'noopener')
    }

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <PartyPopper size={36} color={ACCENT} />
            </Box>
            <Typography sx={{ ...s.title, textAlign: 'center' }}>Your Vibio page is live! 🥳</Typography>
            <Typography sx={{ ...s.subtitle, textAlign: 'center' }}>
                Here's your page. Copy your link and share it in your bio!
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Box sx={previewFrame}>
                    {username && (
                        <iframe
                            src={`/${username}?preview=true&mobile=true`}
                            frameBorder="0"
                            allow="autoplay"
                            scrolling="no"
                            style={{
                                width: "100%",
                                height: "100%",
                                border: 'none',
                                pointerEvents: 'none',
                                display: 'block',
                            }}
                        />
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    backgroundColor: '#f4f4f4',
                    borderRadius: '999px',
                    py: 1,
                    px: 2,
                    my: 2,
                }}
            >
                <Typography sx={{ fontFamily: 'Catamaran', fontWeight: 700 }} noWrap>
                    vibio.co/{username}
                </Typography>
            </Box>

            <Button
                variant="contained"
                disableElevation
                onClick={handleCopy}
                startIcon={copied ? <Check size={18} /> : <Copy size={18} />}
                sx={{
                    ...s.ctaButton,
                    width: 'auto',
                    backgroundColor: '#000',
                    color: '#fcfcfc',
                    boxShadow: `4px 5px 0px ${ACCENT}`,
                    '&:hover': { backgroundColor: '#000', boxShadow: `4px 5px 0px ${ACCENT}` },
                }}
            >
                {copied ? 'Copied!' : 'Copy my link'}
            </Button>

            <Button onClick={handleView} startIcon={<ExternalLink size={18} />} sx={{ ...s.skipButton, mt: 1 }}>
                View my page
            </Button>

            <Box sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={goNext}
                    sx={{ ...s.ctaButton, py: '1rem' }}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    )
}
